import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import { Spinner } from '@/components/atoms/Spinner';
import EmptyState from '@/components/molecules/EmptyState';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import SomethingHappened from '@/components/SomethingHappened';
import {
    FISH_GROUP_CORRECTION_EVENT_TYPES,
    type FishGroupEventType,
    type FishGroupTransferEvent,
    isRevertibleOperation,
    useGetFishGroupEvents,
} from '@/domain/fishGroups';
import { formatDateTimeWorded } from '@/utils/date';

import RevertOperationModal from '../../actions/revert/RevertOperationModal';

import MetadataChangeDetails from './MetadataChangeDetails';

import styles from '../FishGroupDetailModal.module.scss';

interface Props {
    clientId: number;
    fishGroupId: number;
}

const ROW_HEIGHT = 44;
const PLACEHOLDER = '—';

/**
 * Every type that can appear in history — deliberately NOT derived from
 * `FISH_GROUP_MANUAL_EVENT_TYPES`. That list is what the UI lets you *record*
 * (delousing + partial harvest); this filter has to keep offering the types that
 * already exist in the data, including the ones no longer produced
 * (mortality / measurement / feeding / treatment).
 */
const ALL_EVENT_TYPES: FishGroupEventType[] = [
    'created',
    'closed',
    'relocated',
    'discarded',
    'mortality',
    'harvest',
    'measurement',
    'feeding',
    'treatment',
    'delousing',
    'reverted',
    ...FISH_GROUP_CORRECTION_EVENT_TYPES,
];

export default function EventsTab(props: Props) {
    const { clientId, fishGroupId } = props;
    const { t } = useTranslation('fishGroups');
    const [eventTypeFilter, setEventTypeFilter] = useState<FishGroupEventType | ''>('');
    const [revertOperationId, setRevertOperationId] = useState<number | null>(null);
    const filters = eventTypeFilter ? { eventType: eventTypeFilter } : undefined;
    const {
        data: events,
        isLoading,
        error,
    } = useGetFishGroupEvents(clientId, fishGroupId, filters);

    if (isLoading) return <Spinner size="lg" />;
    if (error) return <SomethingHappened />;

    const columns = eventColumns(t);

    return (
        <div>
            <FilterBar
                value={eventTypeFilter}
                onChange={setEventTypeFilter}
                allLabel={t('detail.events.allTypes')}
            />
            {events.length === 0 ? (
                <EmptyState title={t('detail.events.empty')} appearance="quiet" />
            ) : (
                <DataTable
                    data={events}
                    columns={columns}
                    getRowId={(event) => String(event.id)}
                    layout="grid"
                    enableSorting={false}
                    rowHeight={ROW_HEIGHT}
                    getRowProps={() => ({ disableHover: true })}
                    getRowCanExpand={(row) => canExpand(row.original)}
                    renderExpanded={(row) => (
                        <EventDetails
                            event={row.original}
                            onRevertOperation={setRevertOperationId}
                        />
                    )}
                />
            )}
            {revertOperationId !== null && (
                <RevertOperationModal
                    clientId={clientId}
                    operationId={revertOperationId}
                    isOpen
                    onClose={() => setRevertOperationId(null)}
                />
            )}
        </div>
    );
}

interface FilterBarProps {
    value: FishGroupEventType | '';
    onChange: (value: FishGroupEventType | '') => void;
    allLabel: string;
}

function FilterBar(props: FilterBarProps) {
    const { value, onChange, allLabel } = props;
    const { t } = useTranslation('fishGroups');
    const selectId = useId().replace(/:/g, '');
    return (
        <FormField
            layout="inline"
            htmlFor={selectId}
            label={t('detail.events.filterLabel')}
            className={styles.eventFilter}>
            <Select
                id={selectId}
                className={styles.eventFilterSelect}
                value={value}
                onChange={(e) => onChange(e.target.value as FishGroupEventType | '')}>
                <option value="">{allLabel}</option>
                {ALL_EVENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                        {t(`eventTypes.${type}`)}
                    </option>
                ))}
            </Select>
        </FormField>
    );
}

function canExpand(event: FishGroupTransferEvent): boolean {
    const hasDetails = event.eventData != null && Object.keys(event.eventData).length > 0;
    return hasDetails || event.operation != null;
}

function eventColumns(t: TFunction<'fishGroups'>): DataTableColumnDef<FishGroupTransferEvent>[] {
    const placeholder = <span className={styles.placeholder}>{PLACEHOLDER}</span>;
    return [
        {
            id: 'date',
            header: t('detail.events.col.date'),
            meta: { width: '1.2fr' },
            cell: ({ row }) => formatDateTimeWorded(row.original.eventDate, 'minute'),
        },
        {
            id: 'type',
            header: t('detail.events.col.type'),
            cell: ({ row }) => t(`eventTypes.${row.original.eventType}`),
        },
        {
            id: 'cage',
            header: t('detail.events.col.cage'),
            meta: { width: '0.7fr' },
            cell: ({ row }) => row.original.cageId ?? placeholder,
        },
        {
            id: 'counts',
            header: t('detail.events.col.counts'),
            cell: ({ row: { original: event } }) =>
                event.fishCountBefore != null || event.fishCountAfter != null
                    ? `${event.fishCountBefore ?? PLACEHOLDER} → ${event.fishCountAfter ?? PLACEHOLDER}`
                    : placeholder,
        },
        {
            id: 'reason',
            header: t('detail.events.col.reason'),
            meta: { width: '1.5fr' },
            cell: ({ row }) => row.original.operation?.reason ?? row.original.notes ?? placeholder,
        },
        {
            id: 'expand',
            header: '',
            meta: { width: '0.5fr', headerScreenReaderText: t('detail.events.col.details') },
            cell: ({ row }) =>
                row.getCanExpand() && (
                    <Button variant="link" type="button" onClick={row.getToggleExpandedHandler()}>
                        {row.getIsExpanded() ? t('detail.events.hide') : t('detail.events.show')}
                    </Button>
                ),
        },
    ];
}

interface EventDetailsProps {
    event: FishGroupTransferEvent;
    onRevertOperation: (operationId: number) => void;
}

function EventDetails({ event, onRevertOperation }: EventDetailsProps) {
    const { t } = useTranslation('fishGroups');
    const hasDetails = event.eventData != null && Object.keys(event.eventData).length > 0;
    // A correction renders as a diff table rather than a payload dump. Guarded
    // on a non-empty parse so a malformed payload still falls back to the raw
    // view instead of showing an empty table.
    const metadataChanges = event.metadataChanges;
    const isCorrection = metadataChanges.length > 0;
    const revertibleOperationId =
        event.operation && isRevertibleOperation(event.operation) ? event.operation.id : null;

    return (
        <>
            {event.operation && (
                <dl className={styles.operationSummary}>
                    <dt>{t('detail.events.operationType')}</dt>
                    <dd>{event.operation.operationType}</dd>
                    <dt>{t('detail.events.operationDate')}</dt>
                    <dd>{formatDateTimeWorded(event.operation.operationDate, 'minute')}</dd>
                    <dt>{t('detail.events.operationReason')}</dt>
                    <dd>{event.operation.reason ?? PLACEHOLDER}</dd>
                    <dt>{t('detail.events.operationOperator')}</dt>
                    <dd>{event.operation.operatorId}</dd>
                    {event.operation.revertedByOperationId !== null && (
                        <>
                            <dt>{t('detail.events.revertedBy')}</dt>
                            <dd>#{event.operation.revertedByOperationId}</dd>
                        </>
                    )}
                    {event.operation.revertsOperationId !== null && (
                        <>
                            <dt>{t('detail.events.reverts')}</dt>
                            <dd>#{event.operation.revertsOperationId}</dd>
                        </>
                    )}
                </dl>
            )}
            {revertibleOperationId !== null && (
                <Button
                    variant="link"
                    type="button"
                    onClick={() => onRevertOperation(revertibleOperationId)}>
                    {t('detail.events.revertOperation')}
                </Button>
            )}
            {isCorrection ? (
                <MetadataChangeDetails
                    changes={metadataChanges}
                    operatorName={event.operatorName}
                    operatorId={event.operatorId}
                />
            ) : (
                hasDetails && (
                    <pre className={styles.eventDataBlock}>
                        {JSON.stringify(event.eventData, null, 2)}
                    </pre>
                )
            )}
        </>
    );
}
